<template>
  <div class="mt-4 mb-6 mx-2">
    <div class="ats-body flex gap-4 pt-4 overflow-y-scroll flex-grow">
      <div class="flex-1 treatment-options-wrapper p-1 flex flex-col items-center justify-start">
        <div class="text-button mb-4 pt-2">
          TREATMENTS
        </div>
        <div class="w-full">
          <div v-if="interactWithBoard" class="fixedScrollable fullSize">
            <TextCard
              v-for="(treatment, i) in treatments"
              :key="treatment.id"
              class="mb-2 cursor-pointer mx-1 w-full"
              :class="[{ 'disabledTreatment': !todayAndNotCompleted }]"
              :unripple="!todayAndNotCompleted"
              :context="todayAndNotCompleted"
              :error="getTreatmentError(i)"
              :text="treatment.name"
              @click:card-default="todayAndNotCompleted ? $emit('card-default-click', treatment) : null"
              @click:card-error="todayAndNotCompleted ? $emit('select-treatment', treatment) : null"
              @click:context="todayAndNotCompleted ? $emit('select-treatment', treatment) : null"
            />
          </div>
          <div v-if="!interactWithBoard" class="fixedScrollable fullSize">
            <TextCard
              v-for="(treatment, i) in treatments"
              :key="treatment.id"
              class="mb-2 cursor-pointer mx-1 w-full"
              unripple
              context
              :error="getTreatmentError(i)"
              :text="treatment.name"
            />
          </div>
        </div>
      </div>

      <div class="appointment-wrapper flex-1 flex flex-col items-center p-1">
        <div class="text-button mb-4 pt-2 flex items-center gap-2">
          APPOINTMENT
          <v-icon
            v-if="telehealthRoom"
            color="red-darken-2"
            :size="18"
            title="Online video visit"
          >mdi-video</v-icon>
        </div>
        <div class="w-full bg-[#e6e5e5] rounded mb-2 overflow-y-scroll py-2 px-4 fixedScrollable">
          <div v-if="apptTreatmentList.length" class="w-full h-full">
            <draggable
              v-if="interactWithBoard && showDraggable && apptTreatmentList.length"
              :list="apptTreatmentList"
              class="list-group"
              ghost-class="ghost"
              :move="checkMove"
              @start="$emit('drag-start')"
              @end="$emit('drag-end')"
            >
              <div
                v-for="(treatment, i) in apptTreatmentList"
                :key="`appointment-${i}-${treatment.treatment ? treatment.treatment.uuid : treatment.uuid}`"
              >
                <TextCard
                  v-if="treatment.treatment && treatment.treatment.uuid && !treatment.ended_at"
                  class="list-group-item mb-2 cursor-pointer"
                  remove
                  unripple
                  :selected="!!treatment.ended_at"
                  :text="treatmentName(treatment)"
                  :initials="$filters.initials(treatment.doctor.name)"
                  :initialsColor="treatment.doctor.color"
                  :duration="treatment.duration"
                  @click:remove="$emit('remove-treatment', treatment.uuid)"
                />
              </div>
            </draggable>

            <div v-if="!interactWithBoard">
              <div
                v-for="(treatment, i) in apptTreatmentList"
                :key="`appointment-${i}-${treatment.treatment ? treatment.treatment.uuid : treatment.uuid}`"
              >
                <TextCard
                  v-if="treatment.treatment && treatment.treatment.uuid && !treatment.ended_at"
                  class="list-group-item mb-2 cursor-pointer"
                  unripple
                  :selected="!!treatment.ended_at"
                  :text="treatmentName(treatment)"
                  :initials="$filters.initials(treatment.doctor.name)"
                  :initialsColor="treatment.doctor.color"
                  :duration="treatment.duration"
                />
              </div>
            </div>

            <div
              v-for="(treatment, i) in completedTreatments"
              :key="`appointment-complete-${i}-${treatment.treatment ? treatment.treatment.uuid : treatment.uuid}`"
              class="completed-treatment"
            >
              <TextCard
                v-if="treatment.treatment && treatment.treatment.uuid"
                class="list-group-item mb-2"
                disabled
                :selected="!!treatment.ended_at"
                :text="treatmentName(treatment)"
                :initials="$filters.initials(treatment.doctor.name)"
                :initialsColor="treatment.doctor.color"
                :duration="treatment.duration"
              />
            </div>
          </div>
        </div>

        <v-btn
          class="w-full"
          color="primary"
          :disabled="!canApplyLastAppointment || isProcessing"
          @click="$emit('apply-last-appointment')"
        >
          {{ isProcessing ? 'Processing...' : 'LOAD LAST APPOINTMENT' }}
        </v-btn>

        <div v-if="telehealthRoom" class="telehealth-actions w-full my-2">
          <v-btn
              class="flex-1"
              color="bg-gray-50"
              :href="telehealthRoom.url"
              target="_blank"
              rel="noopener"
          >
            <v-icon
                color="red-darken-2"
                :size="18"
                title="Online video visit"
            >mdi-video</v-icon>&nbsp;
            START VIDEO CALL
          </v-btn>
          <v-btn
              icon
              density="comfortable"
              :title="copyLabel"
              :aria-label="copyLabel"
              @click="$emit('copy-link')"
          >
            <v-icon :color="linkCopied ? 'green-darken-2' : undefined" :size="20">
              {{ linkCopied ? 'mdi-check' : 'mdi-content-copy' }}
            </v-icon>
          </v-btn>
        </div>

        <v-btn
          class="w-full"
          color="bg-gray-50"
          :disabled="!canClearAppointment"
          @click="$emit('clear-appointment')"
        >
          CLEAR
        </v-btn>
      </div>
    </div>

    <div class="mt-6 mb-3 px-2 w-full">
      <div>
        <p class="flag-title mb-3">TYPES AND FLAGS</p>
      </div>
      <div>
        <v-chip
          v-for="(flag, index) in currentOpenFlags"
          :key="`treatment-flags-${index}`"
          class="mr-2 mb-2 flags"
        >
          <v-icon color="red-darken-2" :size="18">mdi-flag</v-icon>
          {{ `${flag.message}${flag.description ? `: ${flag.description}` : ''}` }}
        </v-chip>
        <v-chip
          v-for="(tFlag, index) in currentOpenTractionFlags"
          :key="`treatment-t-flags-${index}`"
          class="mr-2 mb-2 tFlags"
        >
          <v-icon color="green-darken-2" :size="18">mdi-flag</v-icon>
          {{ `${tFlag.message}${tFlag.description ? `: ${tFlag.description}` : ''}` }}
        </v-chip>
        <v-chip
          v-for="(profile, index) in currentOpenProfiles"
          :key="`treatment-profiles-${index}`"
          class="mr-2 mb-2 types"
        >
          {{ profile.name }}
        </v-chip>
      </div>
    </div>
  </div>
</template>

<script lang="ts">
import { defineComponent, PropType } from 'vue';
import { mapGetters } from 'vuex';
import { VDraggable as draggable } from '@/ui';
import TextCard from '@/components/cards/TextCard.vue';

interface Treatment {
  id: string;
  uuid: string;
  name: string;
}

interface AppointmentTreatment {
  uuid: string;
  treatment: { uuid: string; name: string } | null;
  doctor: { name: string; color: string };
  duration: number;
  ended_at: string | null;
  status: string;
}

interface TreatmentDoctor {
  uuid: string;
  hasDoctors: boolean;
}

export default defineComponent({
  name: 'TreatmentsTabContent',
  components: {
    draggable,
    TextCard,
  },
  props: {
    treatments: {
      type: Array as PropType<Treatment[]>,
      required: true,
    },
    treatmentsDoctors: {
      type: Array as PropType<TreatmentDoctor[]>,
      required: true,
    },
    apptTreatmentList: {
      type: Array as PropType<AppointmentTreatment[]>,
      required: true,
    },
    completedTreatments: {
      type: Array as PropType<AppointmentTreatment[]>,
      required: true,
    },
    interactWithBoard: {
      type: Boolean,
      default: false,
    },
    todayAndNotCompleted: {
      type: Boolean,
      default: false,
    },
    showDraggable: {
      type: Boolean,
      default: true,
    },
    canApplyLastAppointment: {
      type: Boolean,
      default: false,
    },
    telehealthRoom: {
      type: Object as PropType<Record<string, any> | null>,
      default: null,
    },
    linkCopied: {
      type: Boolean,
      default: false,
    },
    canClearAppointment: {
      type: Boolean,
      default: false,
    },
    isProcessing: {
      type: Boolean,
      default: false,
    },
  },
  emits: [
    'card-default-click',
    'select-treatment',
    'remove-treatment',
    'apply-last-appointment',
    'clear-appointment',
    'drag-start',
    'drag-end',
    'copy-link',
  ],
  computed: {
    ...mapGetters(['currentOpenFlags', 'currentOpenTractionFlags', 'currentOpenProfiles']),
    copyLabel(): string {
      return this.linkCopied ? 'Join link copied' : 'Copy the join link';
    },
  },
  methods: {
    getTreatmentError(index: number): string {
      const treatmentDoctor = this.treatmentsDoctors[index];
      return treatmentDoctor && !treatmentDoctor.hasDoctors ? 'No practitioners' : '';
    },
    treatmentName(treatment: AppointmentTreatment): string {
      if (!treatment) return '';
      return treatment.treatment ? treatment.treatment.name : 'Update to get correct Title';
    },
    checkMove(e: any) {
      console.log('Future index: ' + e.draggedContext.futureIndex);
    },
  },
});
</script>

<style scoped>
.telehealth-actions {
  display: flex;
  align-items: center;
  gap: 4px;
}

.fixedScrollable {
  height: 350px;
  overflow-y: scroll;
  overflow-x: hidden;
}

.fixedScrollable::-webkit-scrollbar {
  display: block;
  width: 5px;
  background-color: #eaeaea;
  border-radius: 5px;
  padding-left: 5px;
}

.fixedScrollable::-webkit-scrollbar-thumb {
  display: block;
  width: 5px;
  background-color: #4e61b1;
  border-radius: 5px;
  cursor: pointer;
}

.fullSize {
  height: 440px;
  padding-right: 10px;
}

.disabledTreatment {
  box-shadow: none;
  cursor: default;
}

.flag-title {
  color: #000000;
  font-size: 14px;
  font-style: normal;
  font-weight: 700;
  line-height: normal;
}

.flags {
  background-color: #FFDEDE !important;
  color: #AB0000;
  font-weight: 500;
}

.tFlags {
  background-color: #b5f7d3 !important;
  color: #00b050;
  font-weight: 500;
}

.types {
  background-color: #EBE7E7 !important;
  color: #000;
  font-weight: 500;
}
</style>
